import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { useTheme } from '../theme/ThemeProvider';
import { useAudio } from '../audio/useAudio';
import { usePlayerStore } from '../store/player';
import { logout as panorLogout } from '../auth/panor';
import { api } from '../api/client';
import i18n from '../i18n/index';

const PUSH_SUPPORTED = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;

type SupportedLang = 'en' | 'zh-Hans';

export function Settings() {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const audio = useAudio();
  const nav = useNavigate();
  const me = usePlayerStore(s => s.me);
  const reset = usePlayerStore(s => s.reset);

  const [soundOn, setSoundOn] = useState(audio.enabled);
  const [lang, setLang] = useState<SupportedLang>(
    (i18n.language?.startsWith('zh') ? 'zh-Hans' : 'en')
  );
  const [pushStatus, setPushStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied' | 'error'>('idle');

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  const toggleSound = () => {
    if (soundOn) {
      audio.disable();
    } else {
      audio.enable();
    }
    setSoundOn(!soundOn);
  };

  const switchLang = (newLang: SupportedLang) => {
    setLang(newLang);
    localStorage.setItem('stanwars.lang', newLang);
    void i18n.changeLanguage(newLang);
  };

  const handleLogout = () => {
    void panorLogout();
    reset();
    nav('/login');
  };

  const handlePushToggle = async () => {
    if (!PUSH_SUPPORTED) return;
    setPushStatus('requesting');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setPushStatus('denied');
        return;
      }
      const stubSub: PushSubscriptionJSON = {
        endpoint: `${window.location.origin}/push-stub/${me?.playerId ?? 'anon'}`,
        keys: { p256dh: '', auth: '' }
      };
      await api.me.subscribePush(stubSub);
      setPushStatus('granted');
    } catch {
      setPushStatus('error');
    }
  };

  const pushLabel = () => {
    if (!PUSH_SUPPORTED) return t('settings.push_not_supported');
    if (pushStatus === 'requesting') return t('settings.push_requesting');
    if (pushStatus === 'granted') return t('settings.push_granted');
    if (pushStatus === 'denied') return t('settings.push_denied');
    if (pushStatus === 'error') return t('settings.push_error');
    return t('settings.push_enable');
  };

  return (
    <main className="min-h-screen flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Link to="/"><PixelButton variant="ghost" className="text-xs">{t('common.home')}</PixelButton></Link>
        <PixelText size="xl" color="white">{t('settings.title')}</PixelText>
      </div>

      {/* Theme */}
      <PixelPanel>
        <PixelText size="base" color="cyan" className="mb-3 block">{t('settings.display')}</PixelText>
        <div className="flex items-center justify-between">
          <PixelText size="sm" color="white">{t('settings.theme_label', { theme })}</PixelText>
          <PixelButton variant="ghost" className="text-xs" onClick={toggleTheme}>
            {t('settings.toggle_theme')}
          </PixelButton>
        </div>
      </PixelPanel>

      {/* Sound */}
      <PixelPanel>
        <PixelText size="base" color="cyan" className="mb-3 block">{t('settings.sound')}</PixelText>
        <div className="flex items-center justify-between">
          <PixelText size="sm" color="white">
            {t('settings.sound_label', { status: soundOn ? t('settings.sound_on') : t('settings.sound_off') })}
          </PixelText>
          <PixelButton variant="ghost" className="text-xs" onClick={toggleSound}>
            {t('settings.toggle_sound')}
          </PixelButton>
        </div>
      </PixelPanel>

      {/* Language */}
      <PixelPanel>
        <PixelText size="base" color="cyan" className="mb-3 block">{t('settings.language')}</PixelText>
        <div className="flex gap-3 mt-1">
          <PixelButton
            variant={lang === 'en' ? 'primary' : 'ghost'}
            className="text-xs"
            onClick={() => switchLang('en')}
          >
            English
          </PixelButton>
          <PixelButton
            variant={lang === 'zh-Hans' ? 'primary' : 'ghost'}
            className="text-xs"
            onClick={() => switchLang('zh-Hans')}
          >
            简体中文
          </PixelButton>
        </div>
      </PixelPanel>

      {/* Push notifications */}
      <PixelPanel>
        <PixelText size="base" color="cyan" className="mb-3 block">{t('settings.push_notifications')}</PixelText>
        <div className="flex items-center justify-between">
          <PixelText
            size="sm"
            color="white"
            className={!PUSH_SUPPORTED || pushStatus === 'denied' ? 'opacity-50' : undefined}
          >
            {pushLabel()}
          </PixelText>
          <PixelButton
            variant="ghost"
            className="text-xs"
            onClick={handlePushToggle}
            disabled={!PUSH_SUPPORTED || pushStatus === 'requesting' || pushStatus === 'granted'}
          >
            {pushStatus === 'granted' ? t('settings.push_on') : t('settings.push_enable_btn')}
          </PixelButton>
        </div>
      </PixelPanel>

      {/* Account */}
      {me && (
        <PixelPanel>
          <PixelText size="sm" color="white" className="mb-2 block">
            {t('settings.signed_in_as')} {me.displayName}
          </PixelText>
          <PixelButton variant="ghost" onClick={handleLogout}>
            {t('settings.sign_out')}
          </PixelButton>
        </PixelPanel>
      )}
    </main>
  );
}
